import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Form } from "react-bootstrap";
import {
  Settings as SettingsIcon, Building2, Mail, Phone, MapPin,
  Globe, DollarSign, Bell, Save, Upload, Trash2, Palette,
  Clock, MessageSquare, MessageCircle, CreditCard, CalendarCheck, Lock,
  Sparkles, Zap, Link2, Copy, RefreshCw, ToggleRight, UserX, BarChart3, Video,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import Shimmer from "../Common/Shimmer";
import { PageHeader, AppCard, AppCardContent, AppCardHeader, AppCardTitle, AppButton, ColorInput, Separator, Tabs, TabsList, TabsTrigger, TabsContent } from "../ui";
import RazorpaySettings from "./RazorpaySettings";
import QrPaymentSettings from "./QrPaymentSettings";
import WebsiteCheckoutSettings from "./WebsiteCheckoutSettings";
import InvoiceSettings from "./InvoiceSettings";
import AgreementSettings from "./AgreementSettings";
import BankDetailsSettings from "./BankDetailsSettings";
// One panel owns the whole Live Class tab: a provider dropdown, and only the
// chosen provider's settings below it. ZoomSettings / ZohoMeetingSettings /
// GratsMeetSettings are mounted from in there, not here.
import LiveClassSettings from "./LiveClassSettings";
import PublicApiSettings from "./PublicApiSettings";
import CmsWebsiteSettings from "./CmsWebsiteSettings";
import CmsCustomDomainSettings from "./CmsCustomDomainSettings";
import AudienceModeSettings from "./AudienceModeSettings";
import ZohoSettings from "./ZohoSettings";
import LeadSourcesSettings from "./LeadSourcesSettings";
import TelephonySettings from "./TelephonySettings";
import PipelineSettings from "./PipelineSettings";
import AppThemeSettings from "./AppThemeSettings";
import LoginPageSettings from "./LoginPageSettings";
import { hasPermission, isInstituteSuperAdmin } from "../../utils/permissions";
import { invalidateEnquiryOwnership } from "../../hooks/useEnquiryOwnership";
import { useBranding } from "../../contexts/BrandingContext";
import { usePlan } from "../../contexts/PlanContext";
import { getAISettings, updateAISettings, getAIUsage } from "../../utils/selfPacedApi";
import ContentProtectionSettings from "./ContentProtectionSettings";
import CommunicationSettings from "./CommunicationSettings";
import "./settings.scss";

/* ── Toggle Switch ── */
function ToggleSwitch({ checked, onChange, id, disabled }) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      className={`settings-toggle ${checked ? "settings-toggle--on" : ""} ${disabled ? "settings-toggle--disabled" : ""}`}
      onClick={() => !disabled && onChange(!checked)}
    >
      <span className="settings-toggle__thumb" />
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   General Settings Tab
   ══════════════════════════════════════════════════════════════════════════════ */
function GeneralSettings({ settings, setSettings, onSave, saving, institute }) {
  const update = (field) => (e) =>
    setSettings((s) => ({ ...s, [field]: e.target.value }));

  return (
    <>
    <AppCard>
      <AppCardHeader>
        <AppCardTitle>
          <span className="settings-card-title">
            <Building2 size={18} /> Institute Information
          </span>
        </AppCardTitle>
        <p className="settings-card-desc">Basic information about your training institute</p>
      </AppCardHeader>
      <AppCardContent>
        <div className="settings-form-grid">
          <div className="settings-field">
            <label htmlFor="instituteName">Institute Name</label>
            <input
              id="instituteName"
              type="text"
              value={settings.instituteName || ""}
              onChange={update("instituteName")}
              placeholder="Your institute name"
            />
          </div>
          <div className="settings-field">
            <label htmlFor="instituteEmail">Email Address</label>
            <div className="settings-input-icon">
              <Mail size={16} />
              <input
                id="instituteEmail"
                type="email"
                value={settings.instituteEmail || ""}
                onChange={update("instituteEmail")}
                placeholder="info@example.com"
              />
            </div>
          </div>
          <div className="settings-field">
            <label htmlFor="institutePhone">Phone Number</label>
            <div className="settings-input-icon">
              <Phone size={16} />
              <input
                id="institutePhone"
                type="text"
                value={settings.institutePhone || ""}
                onChange={update("institutePhone")}
                placeholder="+91 9876543210"
              />
            </div>
          </div>
          <div className="settings-field">
            <label htmlFor="address">Address</label>
            <div className="settings-input-icon">
              <MapPin size={16} />
              <input
                id="address"
                type="text"
                value={settings.address || ""}
                onChange={update("address")}
                placeholder="123 Tech Park, City"
              />
            </div>
          </div>
        </div>

        <Separator />

        <div className="settings-form-grid">
          <div className="settings-field">
            <label htmlFor="timezone">Timezone</label>
            <div className="settings-input-icon">
              <Globe size={16} />
              <select
                id="timezone"
                value={settings.timezone || "Asia/Kolkata"}
                onChange={update("timezone")}
              >
                <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                <option value="America/New_York">America/New York (EST)</option>
                <option value="Europe/London">Europe/London (GMT)</option>
                <option value="Asia/Dubai">Asia/Dubai (GST)</option>
              </select>
            </div>
          </div>
          <div className="settings-field">
            <label htmlFor="currency">Currency</label>
            <div className="settings-input-icon">
              <DollarSign size={16} />
              <select
                id="currency"
                value={settings.currency || "INR"}
                onChange={update("currency")}
              >
                <option value="INR">INR - Indian Rupee</option>
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="GBP">GBP - British Pound</option>
              </select>
            </div>
          </div>
          <div className="settings-field">
            <label htmlFor="workingHoursStart">Working Hours Start</label>
            <div className="settings-input-icon">
              <Clock size={16} />
              <input
                id="workingHoursStart"
                type="time"
                value={settings.workingHoursStart || "09:00"}
                onChange={update("workingHoursStart")}
              />
            </div>
          </div>
          <div className="settings-field">
            <label htmlFor="workingHoursEnd">Working Hours End</label>
            <div className="settings-input-icon">
              <Clock size={16} />
              <input
                id="workingHoursEnd"
                type="time"
                value={settings.workingHoursEnd || "18:00"}
                onChange={update("workingHoursEnd")}
              />
            </div>
          </div>
        </div>

        <div className="settings-save-row">
          <AppButton onClick={() => onSave("general")} disabled={saving}>
            <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
          </AppButton>
        </div>
      </AppCardContent>
    </AppCard>

    {/* Portal URL — read-only info card */}
    {institute?.portalUrl && (
      <AppCard style={{ marginTop: 16 }}>
        <AppCardHeader>
          <AppCardTitle>
            <span className="settings-card-title">
              <Globe size={18} /> Your Portal
            </span>
          </AppCardTitle>
          <p className="settings-card-desc">Share this link with your students and instructors</p>
        </AppCardHeader>
        <AppCardContent>
          <div className="settings-form-grid">
            <div className="settings-field">
              <label>Portal URL</label>
              <div className="settings-input-icon" style={{ position: 'relative' }}>
                <Globe size={16} />
                <input
                  type="text"
                  value={institute.portalUrl}
                  readOnly
                  onClick={(e) => e.target.select()}
                  style={{ cursor: 'pointer', background: '#f1f5f9' }}
                />
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(institute.portalUrl)}
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '0.75rem',
                  }}
                >
                  Copy
                </button>
              </div>
            </div>
            {institute.instituteCode && (
              <div className="settings-field">
                <label>Institute Code</label>
                <div className="settings-input-icon">
                  <Building2 size={16} />
                  <input
                    type="text"
                    value={institute.instituteCode}
                    readOnly
                    style={{ background: '#f1f5f9' }}
                  />
                </div>
              </div>
            )}
          </div>
        </AppCardContent>
      </AppCard>
    )}

    {/* ── Enquiry Ownership / Visibility (Super Admin tier only) ── */}
    {isInstituteSuperAdmin() && (
      <AppCard style={{ marginTop: 16 }}>
        <AppCardHeader>
          <AppCardTitle>
            <span className="settings-card-title">
              <Lock size={18} /> Enquiry Ownership Control
            </span>
          </AppCardTitle>
          <p className="settings-card-desc">
            Restrict enquiry visibility to assigned admins (CRM-style ownership).
            Only visible to Super Admins.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="settings-form-grid">
            <div className="settings-field settings-field--full">
              <div className="settings-toggle-row">
                <div>
                  <label>Restrict Enquiries to Assigned Admin</label>
                  <p className="settings-field-hint">
                    When enabled, regular admins (Admin / Moderator) can only see
                    enquiries explicitly assigned to them. Unassigned enquiries
                    and enquiries owned by other admins remain hidden — only
                    Super Admins can see and assign them.
                  </p>
                </div>
                <ToggleSwitch
                  id="restrictEnquiryVisibility"
                  checked={!!settings.restrictEnquiryVisibility}
                  onChange={(val) => setSettings((s) => ({ ...s, restrictEnquiryVisibility: val }))}
                />
              </div>
            </div>
          </div>
          <div className="settings-save-row">
            <AppButton onClick={() => onSave("general")} disabled={saving}>
              <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
            </AppButton>
          </div>
        </AppCardContent>
      </AppCard>
    )}

    {/* ── Student Auto-Deactivation ── */}
    <AppCard style={{ marginTop: 16 }}>
      <AppCardHeader>
        <AppCardTitle>
          <span className="settings-card-title">
            <UserX size={18} /> Student Auto-Deactivation
          </span>
        </AppCardTitle>
        <p className="settings-card-desc">
          Automatically deactivate students who have no active courses after a defined period
        </p>
      </AppCardHeader>
      <AppCardContent>
        <div className="settings-form-grid">
          <div className="settings-field settings-field--full">
            <div className="settings-toggle-row">
              <div>
                <label>Enable Auto-Deactivation</label>
                <p className="settings-field-hint">
                  When enabled, students with no active batches will be automatically deactivated
                  after the configured number of days from their last course completion.
                </p>
              </div>
              <ToggleSwitch
                id="autoDeactivateEnabled"
                checked={!!settings.autoDeactivateEnabled}
                onChange={(val) => setSettings((s) => ({ ...s, autoDeactivateEnabled: val }))}
              />
            </div>
          </div>
          {settings.autoDeactivateEnabled && (
            <div className="settings-field">
              <label htmlFor="autoDeactivateDays">Deactivate After (days)</label>
              <div className="settings-input-icon">
                <Clock size={16} />
                <input
                  id="autoDeactivateDays"
                  type="number"
                  min="1"
                  max="365"
                  value={settings.autoDeactivateDays || 60}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, autoDeactivateDays: parseInt(e.target.value) || 60 }))
                  }
                  placeholder="60"
                />
              </div>
              <span className="settings-field-hint">
                Days after last course completion before student is deactivated
              </span>
            </div>
          )}
        </div>
        <div className="settings-save-row">
          <AppButton onClick={() => onSave("general")} disabled={saving}>
            <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
          </AppButton>
        </div>
      </AppCardContent>
    </AppCard>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Student Invite Link Settings
   ══════════════════════════════════════════════════════════════════════════════ */
function InviteSettings({ showToast }) {
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [expiryDate, setExpiryDate] = useState("");
  const [savingExpiry, setSavingExpiry] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadInvite = async () => {
    try {
      const { data } = await apiClient.get("/institute/invite");
      setInvite(data);
      if (data.inviteExpiresAt) {
        setExpiryDate(data.inviteExpiresAt.slice(0, 16)); // datetime-local format
      } else {
        setExpiryDate("");
      }
    } catch {
      // If 403/no permission, just show empty
    }
    setLoading(false);
  };

  useEffect(() => { loadInvite(); }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const payload = {};
      if (expiryDate) payload.expiresAt = new Date(expiryDate).toISOString();
      const { data } = await apiClient.post("/institute/invite/generate", payload);
      setInvite(data);
      showToast("Invite link generated!");
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to generate invite link", "error");
    }
    setGenerating(false);
  };

  const handleToggle = async (enabled) => {
    setToggling(true);
    try {
      await apiClient.patch("/institute/invite/toggle", { enabled });
      setInvite((prev) => ({ ...prev, inviteEnabled: enabled }));
      showToast(enabled ? "Invite link enabled" : "Invite link disabled");
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to toggle invite", "error");
    }
    setToggling(false);
  };

  const handleSaveExpiry = async () => {
    setSavingExpiry(true);
    try {
      const payload = { expiresAt: expiryDate ? new Date(expiryDate).toISOString() : null };
      await apiClient.patch("/institute/invite/expiry", payload);
      showToast("Invite expiry updated!");
      loadInvite();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to update expiry", "error");
    }
    setSavingExpiry(false);
  };

  const handleCopy = () => {
    if (invite?.inviteUrl) {
      navigator.clipboard.writeText(invite.inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) return null;

  return (
    <AppCard style={{ marginTop: 16 }}>
      <AppCardHeader>
        <AppCardTitle>
          <span className="settings-card-title">
            <Link2 size={18} /> Student Invite Link
          </span>
        </AppCardTitle>
        <p className="settings-card-desc">
          Generate a shareable link so students can self-register for your institute
        </p>
      </AppCardHeader>
      <AppCardContent>
        {!invite?.inviteCode ? (
          /* No invite code yet — show generate button */
          <div>
            <div className="settings-field invite-expiry-init">
              <label htmlFor="inviteExpiryInit">Expiry Date (optional)</label>
              <input
                id="inviteExpiryInit"
                type="datetime-local"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
            </div>
            <AppButton onClick={handleGenerate} disabled={generating}>
              <Link2 size={16} /> {generating ? "Generating…" : "Generate Invite Link"}
            </AppButton>
          </div>
        ) : (
          /* Invite code exists — show management UI */
          <div>
            {/* Invite URL with copy */}
            <div className="settings-field invite-url-field">
              <label>Invite Link</label>
              <div className="invite-url-row">
                <input
                  type="text"
                  value={invite.inviteUrl || ""}
                  readOnly
                  onClick={(e) => e.target.select()}
                  className="invite-url-input"
                />
                <AppButton
                  onClick={handleCopy}
                  className="invite-copy-btn"
                  title="Copy link"
                >
                  <Copy size={14} /> {copied ? "Copied!" : "Copy"}
                </AppButton>
              </div>
            </div>

            {/* Enable/Disable toggle */}
            <div className="invite-toggle-row">
              <ToggleSwitch
                checked={!!invite.inviteEnabled}
                onChange={(val) => handleToggle(val)}
                id="inviteToggle"
              />
              <label htmlFor="inviteToggle" className="invite-toggle-label">
                {invite.inviteEnabled ? "Invite link is active" : "Invite link is disabled"}
              </label>
              {toggling && <span className="invite-saving-hint">Saving…</span>}
            </div>

            {/* Expired badge */}
            {invite.isExpired && (
              <div className="invite-expired-badge">
                This invite link has expired. Update the expiry date or remove it to re-enable.
              </div>
            )}

            {/* Expiry date */}
            <div className="invite-expiry-row">
              <div className="settings-field invite-expiry-field">
                <label htmlFor="inviteExpiry">Expiry Date</label>
                <input
                  id="inviteExpiry"
                  type="datetime-local"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                />
                <span className="invite-field-hint">Leave empty for no expiry</span>
              </div>
              <AppButton onClick={handleSaveExpiry} disabled={savingExpiry} className="invite-expiry-btn">
                <Save size={14} /> {savingExpiry ? "Saving…" : "Update Expiry"}
              </AppButton>
            </div>

            {/* Regenerate */}
            <Separator />
            <div className="invite-regenerate-row">
              <AppButton
                onClick={handleGenerate}
                disabled={generating}
                className="invite-regenerate-btn"
              >
                <RefreshCw size={14} /> {generating ? "Generating…" : "Regenerate Link"}
              </AppButton>
              <span className="invite-field-hint">This will invalidate the old link</span>
            </div>
          </div>
        )}
      </AppCardContent>
    </AppCard>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Notification Settings Tab
   ══════════════════════════════════════════════════════════════════════════════ */
function NotificationSettings({ settings, setSettings, onSave, saving }) {
  const toggle = (field) => (val) =>
    setSettings((s) => ({ ...s, [field]: val }));

  // Nested toggle for reportEmails.{weekly,monthly}
  const toggleReport = (key) => (val) =>
    setSettings((s) => ({ ...s, reportEmails: { ...(s.reportEmails || {}), [key]: val } }));

  const notifItems = [
    {
      id: "emailNotifications",
      icon: Mail,
      label: "Email Notifications",
      desc: "Receive email notifications for important updates",
      value: settings.emailNotifications ?? true,
    },
    {
      id: "smsNotifications",
      icon: MessageSquare,
      label: "SMS Notifications",
      desc: "Receive SMS alerts for critical events",
      value: settings.smsNotifications ?? false,
    },
    {
      id: "enquiryAlerts",
      icon: Bell,
      label: "New Enquiry Alerts",
      desc: "Get notified when a new enquiry is submitted",
      value: settings.enquiryAlerts ?? true,
    },
    {
      id: "paymentReminders",
      icon: CreditCard,
      label: "Payment Reminders",
      desc: "Send automatic fee reminders to students",
      value: settings.paymentReminders ?? true,
    },
    {
      id: "batchStartNotifications",
      icon: CalendarCheck,
      label: "Batch Start Notifications",
      desc: "Notify students before batch starts",
      value: settings.batchStartNotifications ?? true,
    },
    {
      id: "classStartEmails",
      icon: Video,
      label: "Live Class Start Emails",
      desc: "Email every active student of a batch with a join link the moment their online class starts",
      value: settings.classStartEmails ?? true,
    },
    {
      id: "whatsappNotifications",
      icon: MessageCircle,
      label: "WhatsApp Notifications",
      desc: "Send automated reminders via WhatsApp (class, fee, assignment)",
      value: settings.whatsappNotifications ?? false,
    },
    {
      id: "reportEmailWeekly",
      icon: BarChart3,
      label: "Weekly Report Email",
      desc: "Receive a weekly analytics digest (students, fees, enquiries, batches) every Monday",
      value: settings.reportEmails?.weekly ?? true,
      onToggle: toggleReport("weekly"),
    },
    {
      id: "reportEmailMonthly",
      icon: BarChart3,
      label: "Month-End Report Email",
      desc: "Receive a month-end analytics summary on the 1st of each month",
      value: settings.reportEmails?.monthly ?? true,
      onToggle: toggleReport("monthly"),
    },
  ];

  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle>
          <span className="settings-card-title">
            <Bell size={18} /> Notification Preferences
          </span>
        </AppCardTitle>
        <p className="settings-card-desc">Configure how you receive notifications</p>
      </AppCardHeader>
      <AppCardContent>
        <div className="settings-notif-list">
          {notifItems.map((item, idx) => {
            const Icon = item.icon;
            return (
              <React.Fragment key={item.id}>
                {idx > 0 && <Separator />}
                <div className="settings-notif-row">
                  <div className="settings-notif-info">
                    <div className="settings-notif-icon">
                      <Icon size={16} />
                    </div>
                    <div>
                      <p className="settings-notif-label">{item.label}</p>
                      <p className="settings-notif-desc">{item.desc}</p>
                    </div>
                  </div>
                  <ToggleSwitch
                    id={item.id}
                    checked={item.value}
                    onChange={item.onToggle || toggle(item.id)}
                  />
                </div>
              </React.Fragment>
            );
          })}
        </div>

        <div className="settings-save-row">
          <AppButton onClick={() => onSave("notifications")} disabled={saving}>
            <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
          </AppButton>
        </div>
      </AppCardContent>
    </AppCard>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Branding Settings Tab
   ══════════════════════════════════════════════════════════════════════════════ */
function BrandingSettings({ settings, setSettings, logoUrl, faviconUrl, pwaIconUrl, pwaSplashUrl, onLogoUpload, onLogoDelete, onFaviconUpload, onFaviconDelete, onPwaIconUpload, onPwaIconDelete, onPwaSplashUpload, onPwaSplashDelete, onSave, saving }) {
  const logoFileRef = useRef(null);
  const faviconFileRef = useRef(null);
  const pwaIconFileRef = useRef(null);
  const pwaSplashFileRef = useRef(null);

  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await onLogoUpload(file);
    e.target.value = "";
  };

  const handleFaviconFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await onFaviconUpload(file);
    e.target.value = "";
  };

  const handlePwaIconFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await onPwaIconUpload(file);
    e.target.value = "";
  };

  const handlePwaSplashFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await onPwaSplashUpload(file);
    e.target.value = "";
  };

  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle>
          <span className="settings-card-title">
            <Palette size={18} /> Branding
          </span>
        </AppCardTitle>
        <p className="settings-card-desc">Customize the look and feel of your LMS</p>
      </AppCardHeader>
      <AppCardContent>
        {/* Brand Name & Subtitle */}
        <div className="settings-form-grid">
          <div className="settings-field">
            <label htmlFor="brandName">Brand Name</label>
            <input
              id="brandName"
              type="text"
              placeholder="e.g. My Academy"
              value={settings.brandName || ""}
              onChange={(e) =>
                setSettings((s) => ({ ...s, brandName: e.target.value }))
              }
              maxLength={60}
            />
            <p className="settings-logo-hint">Shown on login page, sidebar, and emails. Leave blank to use your institute's name.</p>
          </div>

          <div className="settings-field">
            <label htmlFor="brandSubtitle">Brand Subtitle</label>
            <input
              id="brandSubtitle"
              type="text"
              placeholder="e.g. Learning Management"
              value={settings.brandSubtitle || ""}
              onChange={(e) =>
                setSettings((s) => ({ ...s, brandSubtitle: e.target.value }))
              }
              maxLength={80}
            />
            <p className="settings-logo-hint">Short tagline shown below the brand name in the sidebar.</p>
          </div>
        </div>

        {/* Company legal name — defaults to the brand name so there are no
            duplicates to maintain; toggle off to set a distinct legal entity. */}
        <div className="settings-field settings-field--full">
          <div className="settings-toggle-row">
            <div>
              <label htmlFor="legalNameSameAsBrand">Use brand name as legal name</label>
              <p className="settings-field-hint">
                Your registered company/legal entity — used on invoices, receipts &amp; search
                listings. Turn this off to set a different one (e.g. brand "Google" → legal "Alphabet Inc.").
              </p>
            </div>
            <ToggleSwitch
              id="legalNameSameAsBrand"
              checked={settings.legalNameSameAsBrand ?? !settings.legalName}
              onChange={(val) =>
                setSettings((s) => ({
                  ...s,
                  legalNameSameAsBrand: val,
                  legalName: val ? "" : s.legalName,
                }))
              }
            />
          </div>
          {!(settings.legalNameSameAsBrand ?? !settings.legalName) && (
            <div className="settings-field" style={{ marginTop: 12 }}>
              <label htmlFor="legalName">Company Legal Name</label>
              <input
                id="legalName"
                type="text"
                placeholder="e.g. Alphabet Inc."
                value={settings.legalName || ""}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, legalName: e.target.value }))
                }
                maxLength={120}
              />
            </div>
          )}
        </div>

        <Separator />

        <div className="settings-branding-pair">
        <div className="settings-branding-section">
          <label className="settings-label">Full Logo</label>
          <div className="settings-logo-row">
            <div className="settings-logo-preview">
              {logoUrl ? (
                <img src={logoUrl} alt="Institute logo" />
              ) : (
                <Building2 size={32} />
              )}
            </div>
            <div className="settings-logo-actions">
              <input
                ref={logoFileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: "none" }}
                onChange={handleLogoFileChange}
              />
              <AppButton
                variant="outline"
                size="sm"
                onClick={() => logoFileRef.current?.click()}
              >
                <Upload size={14} /> Upload Logo
              </AppButton>
              {logoUrl && (
                <AppButton
                  variant="outline"
                  size="sm"
                  className="settings-logo-delete-btn"
                  onClick={onLogoDelete}
                >
                  <Trash2 size={14} /> Remove
                </AppButton>
              )}
              <p className="settings-logo-hint">
                Used in emails, sidebar, and desktop login. Recommended: wide banner, PNG or JPG.
              </p>
            </div>
          </div>
        </div>

        <div className="settings-branding-section">
          <label className="settings-label">Favicon / App Icon</label>
          <div className="settings-logo-row">
            <div className="settings-logo-preview settings-favicon-preview">
              {faviconUrl ? (
                <img src={faviconUrl} alt="Institute favicon" />
              ) : (
                <Building2 size={24} />
              )}
            </div>
            <div className="settings-logo-actions">
              <input
                ref={faviconFileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/x-icon,image/svg+xml"
                style={{ display: "none" }}
                onChange={handleFaviconFileChange}
              />
              <AppButton
                variant="outline"
                size="sm"
                onClick={() => faviconFileRef.current?.click()}
              >
                <Upload size={14} /> Upload Favicon
              </AppButton>
              {faviconUrl && (
                <AppButton
                  variant="outline"
                  size="sm"
                  className="settings-logo-delete-btn"
                  onClick={onFaviconDelete}
                >
                  <Trash2 size={14} /> Remove
                </AppButton>
              )}
              <p className="settings-logo-hint">
                Small square icon for browser tab and mobile. Recommended: 64×64px or 128×128px, PNG or ICO.
              </p>
            </div>
          </div>
        </div>

        </div>

        <Separator />

        <div className="settings-branding-pair">
        {/* ── PWA App Icon (installed app icon + splash) ── */}
        <div className="settings-branding-section">
          <label className="settings-label">App Icon (PWA)</label>
          <div className="settings-logo-row">
            <div className="settings-logo-preview settings-favicon-preview">
              {pwaIconUrl ? (
                <img src={pwaIconUrl} alt="PWA app icon" />
              ) : (
                <Building2 size={24} />
              )}
            </div>
            <div className="settings-logo-actions">
              <input
                ref={pwaIconFileRef}
                type="file"
                accept="image/png,image/webp,image/svg+xml"
                style={{ display: "none" }}
                onChange={handlePwaIconFileChange}
              />
              <AppButton
                variant="outline"
                size="sm"
                onClick={() => pwaIconFileRef.current?.click()}
              >
                <Upload size={14} /> Upload App Icon
              </AppButton>
              {pwaIconUrl && (
                <AppButton
                  variant="outline"
                  size="sm"
                  className="settings-logo-delete-btn"
                  onClick={onPwaIconDelete}
                >
                  <Trash2 size={14} /> Remove
                </AppButton>
              )}
              <p className="settings-logo-hint">
                Icon shown when the app is installed to a phone's home screen (and on the Android splash).
                Recommended: square 512×512px PNG with no transparency. Falls back to the favicon when unset.
              </p>
            </div>
          </div>
        </div>

        {/* ── PWA Splash Logo (centered on the splash / iOS startup screen) ── */}
        <div className="settings-branding-section">
          <label className="settings-label">Splash Logo</label>
          <div className="settings-logo-row">
            <div className="settings-logo-preview">
              {pwaSplashUrl ? (
                <img src={pwaSplashUrl} alt="PWA splash logo" />
              ) : (
                <Building2 size={32} />
              )}
            </div>
            <div className="settings-logo-actions">
              <input
                ref={pwaSplashFileRef}
                type="file"
                accept="image/png,image/webp,image/svg+xml"
                style={{ display: "none" }}
                onChange={handlePwaSplashFileChange}
              />
              <AppButton
                variant="outline"
                size="sm"
                onClick={() => pwaSplashFileRef.current?.click()}
              >
                <Upload size={14} /> Upload Splash Logo
              </AppButton>
              {pwaSplashUrl && (
                <AppButton
                  variant="outline"
                  size="sm"
                  className="settings-logo-delete-btn"
                  onClick={onPwaSplashDelete}
                >
                  <Trash2 size={14} /> Remove
                </AppButton>
              )}
              <p className="settings-logo-hint">
                Logo centered on the launch/splash screen (used for the iOS startup screen on the brand
                background colour). Recommended: transparent PNG. Falls back to the App Icon when unset.
              </p>
            </div>
          </div>
        </div>

        </div>

        <Separator />

        {/* ── App colours (LMS only) ──────────────────────────────────────
            ⚠️ `settings.primaryColor` is NOT here. That field is the public
            WEBSITE's brand colour — vc_cms reads it from /site-config — and it
            used to double as the app's, so retinting the dashboard repainted the
            marketing site and uploading a logo switched on a colour override
            nobody had chosen. The app's own theme is `settings.appTheme`. */}
        <AppThemeSettings
          value={settings.appTheme}
          onChange={(appTheme) => setSettings((s) => ({ ...s, appTheme }))}
        />

        <Separator />

        <div className="settings-branding-pair">
        <div className="settings-branding-section">
          <label className="settings-label" htmlFor="primaryColor">Website Colour</label>
          <p className="settings-logo-hint" style={{ marginTop: 0 }}>
            The brand colour of your public marketing website, and the browser theme colour when the
            app is installed. Separate from the App Colours above.
          </p>
          <ColorInput
            id="primaryColor"
            aria-label="Website colour"
            value={settings.primaryColor || ""}
            fallback="#2463eb"
            onChange={(hex) => setSettings((s) => ({ ...s, primaryColor: hex }))}
          />
        </div>

        <div className="settings-branding-section">
          <label className="settings-label" htmlFor="logoBgColor">Logo Background Color</label>
          <p className="settings-logo-hint" style={{ marginTop: 0 }}>Background color behind the logo icon in the sidebar. Match it to your logo for a polished look.</p>
          <ColorInput
            id="logoBgColor"
            aria-label="Logo background colour"
            value={settings.logoBgColor || ""}
            fallback="#3b82f6"
            onChange={(hex) => setSettings((s) => ({ ...s, logoBgColor: hex }))}
            onReset={() => setSettings((s) => ({ ...s, logoBgColor: "" }))}
          />
        </div>

        </div>

        <Separator />

        {/* ── Email Branding ── */}
        <div className="settings-branding-section">
          <label className="settings-label">Email Branding</label>
          <p className="settings-logo-hint" style={{ marginTop: 0 }}>
            Control how your institute appears in outgoing emails to students and instructors.
          </p>

          <div className="d-flex align-items-center gap-3 mb-3">
            <Form.Check
              type="switch"
              id="emailBrandingEnabled"
              label="Use institute branding in emails"
              checked={settings.emailBranding?.enabled ?? true}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  emailBranding: { ...s.emailBranding, enabled: e.target.checked },
                }))
              }
            />
          </div>

          {(settings.emailBranding?.enabled ?? true) && (
            <>
              <div className="mb-3">
                <label className="settings-label" htmlFor="emailLogoType">Logo in Emails</label>
                <Form.Select
                  size="sm"
                  id="emailLogoType"
                  value={settings.emailBranding?.logoType || "full"}
                  onChange={(e) =>
                    setSettings((s) => ({
                      ...s,
                      emailBranding: { ...s.emailBranding, logoType: e.target.value },
                    }))
                  }
                  style={{ maxWidth: 280 }}
                >
                  <option value="full">Full Logo</option>
                  <option value="icon">Icon Only (small)</option>
                  <option value="none">No Logo</option>
                </Form.Select>
              </div>

              <div className="mb-3">
                <label className="settings-label" htmlFor="customSenderName">Custom Sender Name</label>
                <p className="settings-logo-hint" style={{ marginTop: 0 }}>
                  Override the &quot;From&quot; display name in emails. Leave blank to use your institute name.
                </p>
                <Form.Control
                  size="sm"
                  id="customSenderName"
                  value={settings.emailBranding?.customSenderName || ""}
                  onChange={(e) =>
                    setSettings((s) => ({
                      ...s,
                      emailBranding: { ...s.emailBranding, customSenderName: e.target.value },
                    }))
                  }
                  placeholder={settings.brandName || settings.instituteName || "Institute Name"}
                  maxLength={100}
                  style={{ maxWidth: 350 }}
                />
              </div>
            </>
          )}
        </div>

        <div className="settings-save-row">
          <AppButton onClick={() => onSave("branding")} disabled={saving}>
            <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
          </AppButton>
        </div>
      </AppCardContent>
    </AppCard>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   AI Settings Tab
   ══════════════════════════════════════════════════════════════════════════════ */
function AISettingsTab({ showToast }) {
  const [aiSettings, setAiSettings] = useState({
    aiEnabled: false,
    aiAutoExplainEnabled: false,
  });
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [settingsRes, usageRes] = await Promise.allSettled([
          getAISettings(),
          getAIUsage(),
        ]);
        if (settingsRes.status === "fulfilled") {
          const d = settingsRes.value.data?.data || settingsRes.value.data;
          setAiSettings({
            aiEnabled: d.aiEnabled ?? false,
            aiAutoExplainEnabled: d.aiAutoExplainEnabled ?? false,
          });
        }
        if (usageRes.status === "fulfilled") {
          setUsage(usageRes.value.data?.data || usageRes.value.data);
        }
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAISettings({
        aiEnabled: aiSettings.aiEnabled,
        aiAutoExplainEnabled: aiSettings.aiAutoExplainEnabled,
      });
      showToast("AI settings saved!");
    } catch {
      showToast("Failed to save AI settings", "error");
    }
    setSaving(false);
  };

  // A form of labelled fields, so the skeleton is field-height rows.
  if (loading) return <div className="settings-loading"><Shimmer height={52} count={5} /></div>;

  // ── Usage computations ──
  const adminPool   = usage?.pools?.admin   ?? null;
  const studentPool = usage?.pools?.student ?? null;
  const totalCostUSD = usage?.totalCostUSD ?? 0;
  const featureBreakdown = usage?.featureBreakdown ?? [];

  const fmtTokens = (n) => {
    if (n === -1) return "Unlimited";
    if (n == null || n === 0) return "—";
    return n.toLocaleString();
  };

  const poolPct = (used, assigned) => {
    if (!assigned || assigned === -1 || assigned === 0) return 0;
    return Math.min(100, Math.round((used / assigned) * 100));
  };

  return (
    <>
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="settings-card-title">
              <Sparkles size={18} /> AI Document Explanations
            </span>
          </AppCardTitle>
          <p className="settings-card-desc">
            Automatically generate AI-powered explanations for course documents (PDF, PPT, DOCX, text)
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="settings-notif-list">
            {/* Enable AI */}
            <div className="settings-notif-row">
              <div className="settings-notif-info">
                <div className="settings-notif-icon"><Sparkles size={16} /></div>
                <div>
                  <div className="settings-notif-label">Enable AI Explanations</div>
                  <div className="settings-notif-desc">Allow AI explanation generation for document lessons</div>
                </div>
              </div>
              <ToggleSwitch
                id="aiEnabled"
                checked={aiSettings.aiEnabled}
                onChange={(val) => setAiSettings(s => ({ ...s, aiEnabled: val }))}
              />
            </div>

            <Separator />

            {/* Auto-explain */}
            <div className="settings-notif-row">
              <div className="settings-notif-info">
                <div className="settings-notif-icon"><Zap size={16} /></div>
                <div>
                  <div className="settings-notif-label">Auto-Generate on Upload</div>
                  <div className="settings-notif-desc">Automatically queue AI explanations when a document lesson is confirmed</div>
                </div>
              </div>
              <ToggleSwitch
                id="aiAutoExplain"
                checked={aiSettings.aiAutoExplainEnabled}
                onChange={(val) => setAiSettings(s => ({ ...s, aiAutoExplainEnabled: val }))}
              />
            </div>
          </div>

          <div className="settings-save-row">
            <AppButton onClick={handleSave} disabled={saving}>
              <Save size={16} /> {saving ? "Saving…" : "Save AI Settings"}
            </AppButton>
          </div>
        </AppCardContent>
      </AppCard>

      {/* AI Token Usage card */}
      <AppCard style={{ marginTop: 16 }}>
        <AppCardHeader>
          <AppCardTitle>
            <span className="settings-card-title">
              <Zap size={18} /> AI Token Usage
            </span>
          </AppCardTitle>
          <p className="settings-card-desc">
            Token allocation is set by the platform admin. Resets monthly.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="settings-ai-usage">

            {/* ── Pool cards ── */}
            <div className="settings-ai-pools">
              {/* Admin AI pool */}
              {adminPool && (
                <div className="settings-ai-pool">
                  <div className="settings-ai-pool__head">
                    <span className="settings-ai-pool__name">{adminPool.label}</span>
                    <span className="settings-ai-pool__desc">{adminPool.description}</span>
                  </div>
                  <div className="settings-ai-usage__bar-wrap">
                    <div className="settings-ai-usage__bar">
                      <div
                        className={`settings-ai-usage__bar-fill ${poolPct(adminPool.used, adminPool.assigned) > 90 ? "settings-ai-usage__bar-fill--danger" : ""}`}
                        style={{ width: `${poolPct(adminPool.used, adminPool.assigned)}%` }}
                      />
                    </div>
                    <span className="settings-ai-usage__pct">{poolPct(adminPool.used, adminPool.assigned)}%</span>
                  </div>
                  <div className="settings-ai-pool__stats">
                    <div><span className="settings-ai-usage__label">Used</span><span className="settings-ai-usage__value">{adminPool.used.toLocaleString()}</span></div>
                    <div><span className="settings-ai-usage__label">Assigned</span><span className="settings-ai-usage__value">{fmtTokens(adminPool.assigned)}</span></div>
                  </div>
                </div>
              )}

              {/* Student AI pool */}
              {studentPool && (
                <div className="settings-ai-pool">
                  <div className="settings-ai-pool__head">
                    <span className="settings-ai-pool__name">{studentPool.label}</span>
                    <span className="settings-ai-pool__desc">{studentPool.description}</span>
                  </div>
                  <div className="settings-ai-usage__bar-wrap">
                    <div className="settings-ai-usage__bar">
                      <div
                        className={`settings-ai-usage__bar-fill ${poolPct(studentPool.used, studentPool.assigned) > 90 ? "settings-ai-usage__bar-fill--danger" : ""}`}
                        style={{ width: `${poolPct(studentPool.used, studentPool.assigned)}%` }}
                      />
                    </div>
                    <span className="settings-ai-usage__pct">{poolPct(studentPool.used, studentPool.assigned)}%</span>
                  </div>
                  <div className="settings-ai-pool__stats">
                    <div><span className="settings-ai-usage__label">Used</span><span className="settings-ai-usage__value">{studentPool.used.toLocaleString()}</span></div>
                    <div><span className="settings-ai-usage__label">Assigned</span><span className="settings-ai-usage__value">{fmtTokens(studentPool.assigned)}</span></div>
                  </div>
                </div>
              )}
            </div>

            {/* Per-feature breakdown */}
            {featureBreakdown.length > 0 && (
              <div className="settings-ai-breakdown">
                <div className="settings-ai-breakdown__title">Usage by Feature</div>
                {featureBreakdown.map((row) => (
                  <div key={row.feature} className="settings-ai-breakdown__row">
                    <span className="settings-ai-breakdown__label">{row.label}</span>
                    <span className="settings-ai-breakdown__calls">{row.calls} calls</span>
                    <span className="settings-ai-breakdown__tokens">{row.tokens.toLocaleString()} tokens</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </AppCardContent>
      </AppCard>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Modules Settings Tab — institute admin toggles module visibility for users
   ══════════════════════════════════════════════════════════════════════════════ */

const MODULE_DEFS = [
  { key: 'multiBranch',       label: 'Multi-Branch',        desc: 'Manage multiple branches with branch-wise batches, enquiries, and staff assignment', planKey: 'multiBranch' },
  { key: 'selfPacedCourses',  label: 'Self-Paced Learning',  desc: 'Allow students to browse and enrol in self-paced courses',     planKey: 'selfPacedCourses' },
  { key: 'codingPractice',    label: 'Coding Practice',      desc: 'In-browser coding environment for student practice',          planKey: 'codingPractice' },
  { key: 'aiMock',            label: 'AI Mock Interviews',   desc: 'AI-driven mock interviews with real-time feedback',            planKey: 'aiMock' },
  { key: 'aiLearning',        label: 'AI Learning',          desc: 'Personalised AI tutor for student self-study',                 planKey: 'aiLearning' },
  { key: 'aiQuestionBank',    label: 'AI Question Bank',     desc: 'AI-generated question banks and quiz creation',                planKey: 'aiQuestionBank' },
  { key: 'aiDoubtSolving',    label: 'AIvo – AI Doubt Assistant', desc: 'AI-powered doubt solving chatbot for students',           planKey: 'aiDoubtSolving' },
  { key: 'interviews',        label: 'Interviews',           desc: 'Schedule and manage interview sessions with candidates',       planKey: 'interviews' },
  { key: 'assignments',       label: 'Assignments',          desc: 'Create and manage assignments, grading, and submissions',      planKey: 'assignments' },
  { key: 'feeManagement',     label: 'Fee Management',       desc: 'Fee collection, payment tracking, invoices, and discounts',    planKey: 'feeManagement' },
  { key: 'enquiryManagement', label: 'Enquiry Management',   desc: 'Lead capture, follow-up tracking, and conversion workflows',   planKey: 'enquiryManagement' },
  { key: 'offlineAttendance', label: 'Offline Attendance',   desc: 'Allow instructors to manually mark attendance for students who attended class offline', planKey: null },
  { key: 'attendanceManagement', label: 'Attendance Management (Schools)', desc: 'School/college attendance: meeting-day register, daily absentees, low-attendance reports, guardian alerts, and student leave. Most training institutes leave this off.', planKey: null, defaultOff: true },
  { key: 'advancedStudentProfile', label: 'Advanced Student Profile', desc: 'Previous companies (designation, salary, experience, last working day) and a documents section — Aadhaar, PAN, photo, education certificates and experience letters — on the Add / Edit student form and on the student’s own My Profile page.', planKey: null, defaultOff: true },
  { key: 'contractStudents', label: 'Contract Students (Placement Agreement)', desc: 'Review a student’s details and documents, mark them verified, write the evaluation report (recommended job domain, indicative CTC, programme) and sign a two-sided placement agreement with the student — acknowledged by email code and digital signature, then locked. Needs Advanced Student Profile.', planKey: null, defaultOff: true, requires: 'advancedStudentProfile' },
];

function ModulesSettingsTab({ showToast }) {
  const { planAllowsFeature, refreshPlan } = usePlan();
  const [modules, setModules] = useState({});
  const [allowPlatformCodingQuestions, setAllowPlatformCodingQuestions] = useState(false);
  // Institute-level Smart Learning Flow default (course-level overrides this).
  const [smartLearningFlow, setSmartLearningFlow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get('/institute/settings');
        const em = data.settings?.enabledModules || {};
        // Default each key to true if not explicitly set; defaultOff modules
        // (e.g. attendanceManagement) stay off unless explicitly enabled.
        const init = {};
        MODULE_DEFS.forEach(m => { init[m.key] = m.defaultOff ? em[m.key] === true : em[m.key] !== false; });
        setModules(init);
        setAllowPlatformCodingQuestions(!!data.settings?.allowPlatformCodingQuestions);
        setSmartLearningFlow(!!data.institute?.features?.smartLearningFlow);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const handleToggle = (key, val) => {
    setModules(prev => {
      const next = { ...prev, [key]: val };
      // A module another one needs takes its dependants off with it.
      if (!val) MODULE_DEFS.filter(m => m.requires === key).forEach(m => { next[m.key] = false; });
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/institute/settings', {
        enabledModules: modules,
        allowPlatformCodingQuestions,
        features: { smartLearningFlow },
      });
      showToast('Module visibility settings saved!');
      refreshPlan(); // refresh sidebar immediately
    } catch {
      showToast('Failed to save module settings', 'error');
    }
    setSaving(false);
  };

  if (loading) return <div className="settings-loading"><Shimmer height={52} count={6} /></div>;

  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle>
          <span className="settings-card-title">
            <ToggleRight size={18} /> Module Visibility
          </span>
        </AppCardTitle>
        <p className="settings-card-desc">
          Control which modules are visible to your students and staff.
          Disabled modules will be hidden from the sidebar and all users.
          Modules not included in your plan are locked and cannot be enabled.
        </p>
      </AppCardHeader>
      <AppCardContent>
        <div className="settings-notif-list">
          {MODULE_DEFS.map((mod, idx) => {
            const planAllows = mod.planKey ? planAllowsFeature(mod.planKey) : true;
            const requiredOn = !mod.requires || modules[mod.requires] === true;
            const available = planAllows && requiredOn;
            const codingPracticeOn = planAllowsFeature('codingPractice') && modules.codingPractice !== false;
            return (
              <React.Fragment key={mod.key}>
                {idx > 0 && <Separator />}
                <div className={`settings-notif-row ${!available ? 'settings-notif-row--disabled' : ''} ${mod.requires ? 'settings-notif-row--child' : ''}`}>
                  <div className="settings-notif-info">
                    <div>
                      <div className="settings-notif-label">
                        {mod.label}
                        {!planAllows && (
                          <span className="settings-module-badge">Not in plan</span>
                        )}
                      </div>
                      <div className="settings-notif-desc">{mod.desc}</div>
                    </div>
                  </div>
                  <ToggleSwitch
                    id={`mod-${mod.key}`}
                    checked={available ? modules[mod.key] : false}
                    onChange={(val) => handleToggle(mod.key, val)}
                    disabled={!available}
                  />
                </div>
                {mod.key === 'codingPractice' && (
                  <div className={`settings-notif-row settings-notif-row--child ${!codingPracticeOn ? 'settings-notif-row--disabled' : ''}`}>
                    <div className="settings-notif-info">
                      <div>
                        <div className="settings-notif-label">Allow Platform Coding Questions</div>
                        <div className="settings-notif-desc">
                          Show LMS Campus' curated public coding questions to your students in Coding Practice, in addition to questions you author.
                        </div>
                      </div>
                    </div>
                    <ToggleSwitch
                      id="allowPlatformCodingQuestions"
                      checked={codingPracticeOn ? allowPlatformCodingQuestions : false}
                      onChange={setAllowPlatformCodingQuestions}
                      disabled={!codingPracticeOn}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}

          {/* Smart Learning Flow — institute-wide default (course-level overrides) */}
          <Separator />
          <div className="settings-notif-row">
            <div className="settings-notif-info">
              <div>
                <div className="settings-notif-label">
                  Smart Learning Flow
                  <span className="settings-module-badge">New</span>
                </div>
                <div className="settings-notif-desc">
                  Enable the guided, session-by-session learning experience across all courses by default.
                  Individual courses can override this from their Syllabus page.
                </div>
              </div>
            </div>
            <ToggleSwitch
              id="smartLearningFlow"
              checked={smartLearningFlow}
              onChange={setSmartLearningFlow}
            />
          </div>
        </div>
        <div className="settings-save-row">
          <AppButton onClick={handleSave} disabled={saving}>
            <Save size={16} /> {saving ? 'Saving…' : 'Save Module Settings'}
          </AppButton>
        </div>
      </AppCardContent>
    </AppCard>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Video Security Settings — global toggle for desktop app requirement
   ══════════════════════════════════════════════════════════════════════════════ */
function VideoSecuritySettingsTab({ showToast }) {
  const { hasPlanFeature } = usePlan();
  const planAllows = hasPlanFeature('secureVideoPlayer');
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get('/institute/settings');
        setEnabled(!!data.settings?.videoSecurityEnabled);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/institute/settings', { videoSecurityEnabled: enabled });
      showToast('Video security settings saved!');
    } catch {
      showToast('Failed to save video security settings', 'error');
    }
    setSaving(false);
  };

  if (loading) return <div className="settings-loading"><Shimmer height={52} count={4} /></div>;

  return (
    <AppCard style={{ marginTop: 16 }}>
      <AppCardHeader>
        <AppCardTitle>
          <span className="settings-card-title">
            <Lock size={18} /> Video Security (Desktop App)
          </span>
        </AppCardTitle>
        <p className="settings-card-desc">
          When enabled, students must use the LMS Campus Desktop App to watch videos in courses that have video security turned on.
          This provides OS-level screen capture protection — preventing tools like OBS, screen recorders, and screenshot extensions.
          Download prevention and watermarks are always active for all customers regardless of this setting.
        </p>
      </AppCardHeader>
      <AppCardContent>
        <div className="settings-notif-list">
          <div className={`settings-notif-row ${!planAllows ? 'settings-notif-row--disabled' : ''}`}>
            <div className="settings-notif-info">
              <div>
                <div className="settings-notif-label">
                  Enable Video Security
                  {!planAllows && (
                    <span className="settings-module-badge">Not in plan</span>
                  )}
                </div>
                <div className="settings-notif-desc">
                  Globally enable video security for your institute. You can then turn it on per-course in course settings.
                </div>
              </div>
            </div>
            <ToggleSwitch
              id="videoSecurityEnabled"
              checked={planAllows ? enabled : false}
              onChange={setEnabled}
              disabled={!planAllows}
            />
          </div>
        </div>
        <div className="settings-save-row">
          <AppButton onClick={handleSave} disabled={saving || !planAllows}>
            <Save size={16} /> {saving ? 'Saving…' : 'Save Video Security Settings'}
          </AppButton>
        </div>
      </AppCardContent>
    </AppCard>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Main Settings Page
   ══════════════════════════════════════════════════════════════════════════════ */
const SettingsPage = () => {
  const { refreshBranding } = useBranding();
  const { hasPlanFeature, hasStudentModule } = usePlan();
  const { search } = useLocation();
  const [settings, setSettings] = useState({});
  const [institute, setInstitute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [faviconUrl, setFaviconUrl] = useState(null);
  const [pwaIconUrl, setPwaIconUrl] = useState(null);
  const [pwaSplashUrl, setPwaSplashUrl] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Fetch settings + logo on mount
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [settingsRes, logoRes, faviconRes, pwaIconRes, pwaSplashRes] = await Promise.all([
          apiClient.get("/institute/settings"),
          apiClient.get("/institute/settings/logo-url"),
          apiClient.get("/institute/settings/favicon-url"),
          apiClient.get("/institute/settings/pwa-icon-url"),
          apiClient.get("/institute/settings/pwa-splash-url"),
        ]);
        setSettings(settingsRes.data.settings || {});
        if (settingsRes.data.institute) setInstitute(settingsRes.data.institute);
        setLogoUrl(logoRes.data.url || null);
        setFaviconUrl(faviconRes.data.url || null);
        setPwaIconUrl(pwaIconRes.data.url || null);
        setPwaSplashUrl(pwaSplashRes.data.url || null);
      } catch (err) {
        // Silently handle 403 (permission denied) — tabs will show locked state
        const status = err?.response?.status;
        if (status !== 403) {
          showToast("Failed to load settings", "error");
        }
      }
      setLoading(false);
    };
    load();
  }, []);

  const showToast = (message, type = "success") =>
    setToast({ show: true, message, type });

  // Save handler — sends all settings fields
  const handleSave = async (section) => {
    setSaving(true);
    try {
      await apiClient.put("/institute/settings", settings);
      showToast(`${capitalize(section)} settings saved successfully!`);
      // Enquiry Ownership Control decides whether the enquiry screens offer
      // assign/delete, and that answer is cached per session. Without dropping
      // it here, the Super Admin who just flipped the toggle would keep seeing
      // the old controls until they reopened the tab.
      invalidateEnquiryOwnership();
      // Refresh the global BrandingContext so sidebar/nav/title pick up new
      // names immediately. The "Institute Information" section can change the
      // institute name (and thus the resolved brand name), so refresh on every
      // save, not just the branding section.
      refreshBranding();
    } catch {
      showToast("Failed to save settings", "error");
    }
    setSaving(false);
  };

  // Logo upload via server-side upload
  const handleLogoUpload = async (file) => {
    try {
      showToast("Uploading logo…", "info");
      const formData = new FormData();
      formData.append("file", file);
      await apiClient.post("/institute/settings/logo-upload-url", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const logoRes = await apiClient.get("/institute/settings/logo-url");
      setLogoUrl(logoRes.data.url || null);
      showToast("Logo uploaded successfully!");
      refreshBranding();
    } catch {
      showToast("Failed to upload logo", "error");
    }
  };

  const handleLogoDelete = async () => {
    try {
      await apiClient.delete("/institute/settings/logo");
      setLogoUrl(null);
      showToast("Logo removed");
      refreshBranding();
    } catch {
      showToast("Failed to remove logo", "error");
    }
  };

  // Favicon upload via server-side upload
  const handleFaviconUpload = async (file) => {
    try {
      showToast("Uploading favicon…", "info");
      const formData = new FormData();
      formData.append("file", file);
      await apiClient.post("/institute/settings/favicon-upload-url", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const faviconRes = await apiClient.get("/institute/settings/favicon-url");
      setFaviconUrl(faviconRes.data.url || null);
      showToast("Favicon uploaded successfully!");
      refreshBranding();
    } catch {
      showToast("Failed to upload favicon", "error");
    }
  };

  const handleFaviconDelete = async () => {
    try {
      await apiClient.delete("/institute/settings/favicon");
      setFaviconUrl(null);
      showToast("Favicon removed");
      refreshBranding();
    } catch {
      showToast("Failed to remove favicon", "error");
    }
  };

  // PWA app icon (installed-app icon + splash) via server-side upload
  const handlePwaIconUpload = async (file) => {
    try {
      showToast("Uploading app icon…", "info");
      const formData = new FormData();
      formData.append("file", file);
      await apiClient.post("/institute/settings/pwa-icon-upload-url", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const res = await apiClient.get("/institute/settings/pwa-icon-url");
      setPwaIconUrl(res.data.url || null);
      showToast("App icon uploaded successfully!");
      refreshBranding();
    } catch {
      showToast("Failed to upload app icon", "error");
    }
  };

  const handlePwaIconDelete = async () => {
    try {
      await apiClient.delete("/institute/settings/pwa-icon");
      setPwaIconUrl(null);
      showToast("App icon removed");
      refreshBranding();
    } catch {
      showToast("Failed to remove app icon", "error");
    }
  };

  // PWA splash logo via server-side upload
  const handlePwaSplashUpload = async (file) => {
    try {
      showToast("Uploading splash logo…", "info");
      const formData = new FormData();
      formData.append("file", file);
      await apiClient.post("/institute/settings/pwa-splash-upload-url", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const res = await apiClient.get("/institute/settings/pwa-splash-url");
      setPwaSplashUrl(res.data.url || null);
      showToast("Splash logo uploaded successfully!");
      refreshBranding();
    } catch {
      showToast("Failed to upload splash logo", "error");
    }
  };

  const handlePwaSplashDelete = async () => {
    try {
      await apiClient.delete("/institute/settings/pwa-splash");
      setPwaSplashUrl(null);
      showToast("Splash logo removed");
      refreshBranding();
    } catch {
      showToast("Failed to remove splash logo", "error");
    }
  };

  /* All 5 settings tabs — every admin sees them, but locked tabs show a
     "no permission" message instead of the actual content. */
  /* Each tab may have a `planFeature` — if the plan doesn't include the
     feature the tab is hidden entirely (not just locked). */
  const allTabs = [
    { value: "general",       label: "General",       permission: "settings_general" },
    { value: "modules",       label: "Modules",       permission: "settings_general" },
    { value: "content-protection", label: "Content Protection", permission: "settings_general" },
    { value: "notifications", label: "Notifications", permission: "settings_notifications" },
    // The institute's own mail server and SMS gateway. Credentials, so the
    // same permission as the other integrations — and every endpoint behind it.
    { value: "communications", label: "Email & SMS",  permission: "settings_general" },
    { value: "branding",      label: "Branding",      permission: "settings_branding",  planFeature: "customBranding" },
    { value: "payments",      label: "Payments",      permission: "settings_payments",  planFeature: "feeManagement" },
    // Its own tab, not a card under Payments: the form needs the width for its live preview.
    { value: "invoices",      label: "Invoices",      permission: "settings_payments",  planFeature: "feeManagement" },
    // A module switch, not a plan feature: it appears once Contract Students is saved on.
    { value: "agreements",    label: "Evaluation & Agreement", permission: "settings_general", studentModule: "contractStudents" },
    // The tab holds all three live-class providers, so it cannot be gated on
    // Zoom alone: an institute sold only Grats Meet had no way to reach the
    // settings for the provider it had actually bought.
    { value: "zoom", label: "Live Class", permission: "settings_zoom",
      planFeatureAny: ["zoomV2", "platformHostedZoom", "platformHostedZoho", "gratsMeet"] },
    { value: "api",            label: "Public API",    permission: "settings_general" },
    { value: "zoho",           label: "Zoho CRM",     permission: "settings_general",  planFeature: "zohoCrm" },
    { value: "telephony",     label: "Calling",       permission: "settings_general",  planFeature: "cloudTelephony" },
    { value: "pipeline",      label: "Enquiry Pipeline", permission: "settings_general", planFeature: "enquiryManagement" },
    /* Lead sync is its own plan feature, sold apart from ads publishing
       (`adsManager`, which gates /marketing). The permission is the enquiries
       one, not `settings_general`, because that is what every endpoint behind
       this tab checks — gating the tab on a different key showed an enquiries
       admin no tab, and a settings admin a tab that 403s on load. */
    { value: "lead-sources",  label: "Lead Sources",  permission: "add_edit_enquiries", planFeature: "leadCapture" },
    { value: "ai",            label: "AI",            permission: "settings_general" },
    { value: "website",       label: "Website",       permission: "settings_general",  planFeature: "cmsWebsite" },
    { value: "custom-domain", label: "Custom Domain", permission: "settings_general",  planFeature: "customDomain" },
    { value: "audience",      label: "Audience",      permission: "settings_general",  planFeature: "cbtEnterprise" },
  ];

  // Only show tabs whose plan feature is available (or tabs without a planFeature).
  // `planFeatureAny` is for a tab that several features can unlock — one screen
  // covering three integrations must not be gated on whichever one was listed first.
  const visibleTabs = allTabs.filter((t) => {
    if (t.studentModule && !hasStudentModule(t.studentModule)) return false;
    if (t.planFeatureAny) return t.planFeatureAny.some((f) => hasPlanFeature(f));
    return !t.planFeature || hasPlanFeature(t.planFeature);
  });

  // Pick the first unlocked tab as default; fall back to first visible tab
  const firstUnlocked = visibleTabs.find(t => hasPermission(t.permission));
  const fallbackTab = firstUnlocked ? firstUnlocked.value : (visibleTabs[0]?.value || "general");

  // `?tab=` lets other pages deep-link here (e.g. the Calls page's "Calling
  // settings" button). Only honoured for a tab this admin can actually see, so a
  // stale or hand-typed link degrades to the normal landing tab rather than an
  // empty panel.
  const requestedTab = new URLSearchParams(search).get("tab");
  const defaultTab = visibleTabs.some(t => t.value === requestedTab && hasPermission(t.permission))
    ? requestedTab
    : fallbackTab;

  if (loading) {
    // ⚠️ This one is the whole page, and it was invisible to the visual spec's
    // busy-detector — plain text matches neither `.spinner-border` nor
    // `[class*="animate-shimmer"]`, so `waitForSettled` returned immediately and
    // froze a baseline reading "Loading settings…". A Shimmer is both the right
    // loading state and the thing the detector can see.
    return <div className="settings-loading"><Shimmer height={56} count={6} /></div>;
  }

  /* Locked-tab placeholder shown inside tabs the admin cannot access */
  const LockedTabContent = ({ label }) => (
    <div className="settings-locked">
      <div className="settings-locked__icon">
        <Lock size={32} />
      </div>
      <h3 className="settings-locked__title">Access Restricted</h3>
      <p className="settings-locked__desc">
        You do not have permission to view the <strong>{label}</strong> section.
        Contact your Super Admin to request access.
      </p>
    </div>
  );

  return (
    <div className="settings-page">
      <PageHeader
        icon={SettingsIcon}
        title="Settings"
        description="Manage your institute settings and preferences"
      />

      <Tabs defaultValue={defaultTab} className="settings-tabs">
        <TabsList overflow>
          {visibleTabs.map(t => {
            const locked = !hasPermission(t.permission);
            return (
              <TabsTrigger
                key={t.value}
                value={t.value}
                className={locked ? "settings-tab--locked" : ""}
              >
                {t.label}
                {locked && <Lock size={12} className="settings-tab-lock-icon" />}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Render tab content only for visible (plan‑included) tabs */}
        {visibleTabs.some(t => t.value === "general") && (
          <TabsContent value="general">
            {hasPermission("settings_general") ? (
              <>
                <GeneralSettings
                  settings={settings}
                  setSettings={setSettings}
                  onSave={handleSave}
                  saving={saving}
                  institute={institute}
                />
                <InviteSettings showToast={showToast} />
              </>
            ) : (
              <LockedTabContent label="General" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "modules") && (
          <TabsContent value="modules">
            {hasPermission("settings_general") ? (
              <ModulesSettingsTab showToast={showToast} />
            ) : (
              <LockedTabContent label="Modules" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "content-protection") && (
          <TabsContent value="content-protection">
            {hasPermission("settings_general") ? (
              <>
                <ContentProtectionSettings showToast={showToast} />
                <VideoSecuritySettingsTab showToast={showToast} />
              </>
            ) : (
              <LockedTabContent label="Content Protection" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "notifications") && (
          <TabsContent value="notifications">
            {hasPermission("settings_notifications") ? (
              <NotificationSettings
                settings={settings}
                setSettings={setSettings}
                onSave={handleSave}
                saving={saving}
              />
            ) : (
              <LockedTabContent label="Notifications" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "communications") && (
          <TabsContent value="communications">
            {hasPermission("settings_general") ? (
              <CommunicationSettings
                showToast={showToast}
                brandName={settings.brandName || settings.instituteName || institute?.name}
              />
            ) : (
              <LockedTabContent label="Email & SMS" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "branding") && (
          <TabsContent value="branding">
            {hasPermission("settings_branding") ? (
              <BrandingSettings
                settings={settings}
                setSettings={setSettings}
                logoUrl={logoUrl}
                faviconUrl={faviconUrl}
                pwaIconUrl={pwaIconUrl}
                pwaSplashUrl={pwaSplashUrl}
                onLogoUpload={handleLogoUpload}
                onLogoDelete={handleLogoDelete}
                onFaviconUpload={handleFaviconUpload}
                onFaviconDelete={handleFaviconDelete}
                onPwaIconUpload={handlePwaIconUpload}
                onPwaIconDelete={handlePwaIconDelete}
                onPwaSplashUpload={handlePwaSplashUpload}
                onPwaSplashDelete={handlePwaSplashDelete}
                onSave={handleSave}
                saving={saving}
              />
            ) : (
              <LockedTabContent label="Branding" />
            )}
            {hasPermission("settings_branding") && (
              <div className="mt-4">
                <LoginPageSettings showToast={showToast} />
              </div>
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "payments") && (
          <TabsContent value="payments">
            {hasPermission("settings_payments") ? (
              <>
                <RazorpaySettings />
                <QrPaymentSettings showToast={showToast} />
                {hasPlanFeature("cmsWebsite") && <WebsiteCheckoutSettings showToast={showToast} />}
                <BankDetailsSettings />
              </>
            ) : (
              <LockedTabContent label="Payments" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "invoices") && (
          <TabsContent value="invoices">
            {hasPermission("settings_payments") ? (
              <InvoiceSettings showToast={showToast} />
            ) : (
              <LockedTabContent label="Invoices" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "agreements") && (
          <TabsContent value="agreements">
            {hasPermission("settings_general") ? (
              <AgreementSettings showToast={showToast} />
            ) : (
              <LockedTabContent label="Evaluation & Agreement" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "zoom") && (
          <TabsContent value="zoom">
            {hasPermission("settings_zoom") ? (
              <LiveClassSettings />
            ) : (
              <LockedTabContent label="Live Class" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "api") && (
          <TabsContent value="api">
            {hasPermission("settings_general") ? (
              <PublicApiSettings />
            ) : (
              <LockedTabContent label="Public API" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "zoho") && (
          <TabsContent value="zoho">
            {hasPermission("settings_general") ? (
              <ZohoSettings />
            ) : (
              <LockedTabContent label="Zoho CRM" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "telephony") && (
          <TabsContent value="telephony">
            {hasPermission("settings_general") ? (
              <TelephonySettings />
            ) : (
              <LockedTabContent label="Calling" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "pipeline") && (
          <TabsContent value="pipeline">
            {hasPermission("settings_general") ? (
              <PipelineSettings />
            ) : (
              <LockedTabContent label="Enquiry Pipeline" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "lead-sources") && (
          <TabsContent value="lead-sources">
            {hasPermission("add_edit_enquiries") ? (
              <LeadSourcesSettings />
            ) : (
              <LockedTabContent label="Lead Sources" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "ai") && (
          <TabsContent value="ai">
            {hasPermission("settings_general") ? (
              <AISettingsTab showToast={showToast} />
            ) : (
              <LockedTabContent label="AI" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "website") && (
          <TabsContent value="website">
            {hasPermission("settings_general") ? (
              <CmsWebsiteSettings />
            ) : (
              <LockedTabContent label="Website" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "custom-domain") && (
          <TabsContent value="custom-domain">
            {hasPermission("settings_general") ? (
              <CmsCustomDomainSettings />
            ) : (
              <LockedTabContent label="Custom Domain" />
            )}
          </TabsContent>
        )}

        {visibleTabs.some(t => t.value === "audience") && (
          <TabsContent value="audience">
            {hasPermission("settings_general") ? (
              <AudienceModeSettings />
            ) : (
              <LockedTabContent label="Audience" />
            )}
          </TabsContent>
        )}
      </Tabs>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default SettingsPage;
